import { Link } from 'react-router-dom';
import { CheckCircle2, CircleAlert, LogOut, Printer } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { useAuth } from '@/lib/auth';
import { cn, dateTime } from '@/lib/utils';
import { Button, StatusBadge } from '@/components/ui/primitives';
import { ShellPanel, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionIsOpen, admissionPath, type Admission } from '../types';
import { useAdmissionPrints } from './DocumentsSection';
import { dischargeChecks } from '../workspace-facts';
import { DischargeInsurerDetails } from './DischargeInsurerDetails';

export function DischargeSection({ admission }: { admission: Admission }) {
  const { can, hasModule } = useAuth();
  const { printDoc } = useAdmissionPrints(admission.id);
  const current = admissionIsOpen(admission);
  const checks = dischargeChecks(admission);
  const dischargeTo = `/ipd/admissions/${admission.id}/discharge?returnTo=${encodeURIComponent(admissionPath(admission.id, 'discharge'))}`;

  if (!current) {
    return (
      <>
        <ShellSectionHeading icon={LogOut} title={tr('ipd', 'ws_nav_discharge')} description={tr('ipd', 'ws_left_on_x', { date: dateTime(admission.dischargedAt) })} />
        <ShellPanel
          icon={LogOut}
          title={tr('ipd', 'ws_how_the_stay_ended')}
          actions={
            <Button size="sm" variant="outline" onClick={() => printDoc.mutate('summary')} loading={printDoc.isPending}>
              <Printer className="h-4 w-4" aria-hidden /> {tr('ipd', 'discharge_summary')}
            </Button>
          }
        >
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <StatusBadge status={admission.status} />
            <span className="text-muted-foreground">{dateTime(admission.dischargedAt)}</span>
            <span className="text-muted-foreground">{tr('ipd', 'ws_day_x', { n: admission.lengthOfStayDays })}</span>
          </div>
        </ShellPanel>
        {hasModule('nphies') && admission.payer && <DischargeInsurerDetails admission={admission} />}
      </>
    );
  }

  return (
    <>
      <ShellSectionHeading
        icon={LogOut}
        title={tr('ipd', 'ws_nav_discharge')}
        description={tr('ipd', 'ws_discharge_desc')}
        actions={
          can('admission.manage') ? (
            <Link to={dischargeTo}>
              <Button size="sm">
                <LogOut className="h-4 w-4" aria-hidden /> {tr('ipd', 'discharge')}
              </Button>
            </Link>
          ) : null
        }
      />
      <ShellPanel icon={CheckCircle2} title={tr('ipd', 'ws_before_discharge')}>
        <ul className="space-y-2">
          {checks.map((check) => (
            <li key={check.key} className="flex items-center gap-2 text-sm">
              {check.ok ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
              ) : (
                <CircleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden />
              )}
              <span className={cn(!check.ok && 'font-medium')}>{check.label}</span>
            </li>
          ))}
        </ul>
      </ShellPanel>
    </>
  );
}
